import React, { useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  PersonRecord,
  PersonRecordType,
} from '../../redux/Candidate/Record/Record.type';
import { useTranslation } from 'react-i18next';
import NotAssignComponent from '../NotAssign.component';
import { Job } from '../../redux/Job/Job.type';
import { uniqueId } from 'lodash';
import ButtonComponent, { StyledButton } from '../Button.component';
import RecordResultModalComponent from '../Modal/Candidate/Record/RecordResultModal.component';
import UserBadgeComponent from '../UserBadge.component';
import { useModalManager } from '../../hooks/ModalManager.hook';
import TagBlock from './TagBlock';
import { Tag } from '../../redux/ControlPanel/TagControl/TagControl.type';
import ApiWrapper from '../../hooks/useApi.hook';
import { syncTagsThunk } from '../../redux/Tag/Tag.thunk';
import { TagSyncPayload } from '../../redux/Tag/Tag.type';
import { PERSON_RECORD_ATTACH_OBJECT } from '../../constants/attachment.constant';
import { StyledDivider } from '../../styles';
import { getCandidateRecordListThunk } from '../../redux/Candidate/Record/Record.thunk';
import { checkUserRequiredPermissions } from '../../utils/getUserRolesByToken';
import { UserPermissions } from '../../types/userPermissions.types';
import useAuthHook from '../../hooks/useAuth.hook';
import { copySalary } from '../Modal/Candidate/Record/RecordEditModal.component';
import EyeIcon from '../Icons/Eye.icon';
import EyeCrossedIcon from '../Icons/EyeCrossed.icon';
import GoogleCalendarInterviewBlockComponent from '../Calendar/GoogleCalendarInterviewBlock/GoogleCalendarInterviewBlock.component';

export interface CandidateRecordInfoBlockProps {
  info: PersonRecord;
  withContentButton?: boolean;
  align?: 'left' | 'right';
}

const StyledCandidateRecordInfoResult = styled.div<{
  withContentButton: boolean;
}>`
  ${props => props.withContentButton && 'width: min-content'};
  margin-bottom: 10px;

  ${StyledButton} {
    padding: 10px 50px;
  }
`;
const StyledCandidateRecordInfoJobs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
const StyledTechSpecialistsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
const StyledCandidateRecordInfoRowValue = styled.div<{
  align: string;
}>`
  text-align: ${props => props.align};
`;
const StyledCandidateRecordInfoRowLabel = styled.div`
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledCandidateRecordInfoRowComment = styled.div`
  color: ${({ theme }) => theme.colors.LABEL};
  font-size: 14px;
  line-height: 1.5;
  font-weight: 400;
  white-space: break-spaces;
`;
const StyledCandidateRecordInfoRow = styled.div`
  display: grid;
  grid-template-columns: 120px auto;
  grid-gap: 15px;
  font-size: 12px;
`;
export const StyledCandidateRecordInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-weight: 500;
`;
export const StyledSalaryRecordInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  vertical-align: middle;
`;

const CandidateRecordInfoBlockComponent = ({
  info,
  align = 'left',
  withContentButton = true,
}: CandidateRecordInfoBlockProps) => {
  const { openModal } = useModalManager();
  const { t } = useTranslation();
  const { user } = useAuthHook();
  const theme = useTheme() as any;

  const [isSalaryVisible, setIsSalaryVisible] = useState(false);

  const isRenderResultButton =
    info.type === PersonRecordType.RECORD &&
    info.status &&
    info.status.needAction;

  const renderResultButton = useMemo(() => {
    if (isRenderResultButton)
      return (
        <StyledCandidateRecordInfoResult withContentButton={withContentButton}>
          <ButtonComponent
            handleClick={() =>
              openModal(RecordResultModalComponent, {
                info,
              })
            }
          >
            {t('Result')}
          </ButtonComponent>
        </StyledCandidateRecordInfoResult>
      );
  }, [info]);

  const handleTagsChanged = async (tags: Tag[]) => {
    const payload: TagSyncPayload = {
      objectType: PERSON_RECORD_ATTACH_OBJECT,
      objectId: info.id,
      tags: tags.map(tag => tag.id),
    };
    await ApiWrapper(syncTagsThunk(payload));
    // TODO: выяснить: getCandidateRecordListThunk зачем?
    await ApiWrapper(getCandidateRecordListThunk({ personId: info.personId }));
  };

  const computedSalary = useMemo(() => {
    if (!isSalaryVisible) return '***';

    // Преобразуем из '3500000' к '3 500 000'
    return Number(info.salary).toLocaleString('ru');
  }, [isSalaryVisible, info.salary]);

  const computedSalaryBrutto = useMemo(() => {
    if (!isSalaryVisible) return '***';

    // Преобразуем из '3500000' к '3 500 000'
    return Number(info.salaryBrutto).toLocaleString('ru');
  }, [isSalaryVisible, info.salaryBrutto]);

  return (
    <StyledCandidateRecordInfo>
      {checkUserRequiredPermissions(user.permissions, [
        UserPermissions.CAN_EDIT_CANDIDATE_RECORD,
      ]) && renderResultButton}
      <StyledCandidateRecordInfoRow>
        <StyledCandidateRecordInfoRowLabel>
          {t('Jobs')}:
        </StyledCandidateRecordInfoRowLabel>
        <StyledCandidateRecordInfoRowValue align={align}>
          <NotAssignComponent has={!!info.jobs && info.jobs.length > 0}>
            {info.jobs && (
              <StyledCandidateRecordInfoJobs>
                {info.jobs.map((job: Job) => (
                  <div key={uniqueId(`record-${info.id}-job-`)}>{job.name}</div>
                ))}
              </StyledCandidateRecordInfoJobs>
            )}
          </NotAssignComponent>
        </StyledCandidateRecordInfoRowValue>
      </StyledCandidateRecordInfoRow>
      {info.jobType && (
        <StyledCandidateRecordInfoRow>
          <StyledCandidateRecordInfoRowLabel>
            {t('Job type')}:
          </StyledCandidateRecordInfoRowLabel>
          <StyledCandidateRecordInfoRowValue align={align}>
            {t(info.jobType.name)}
          </StyledCandidateRecordInfoRowValue>
        </StyledCandidateRecordInfoRow>
      )}
      <StyledCandidateRecordInfoRow>
        <StyledCandidateRecordInfoRowLabel>
          {t('Recruiter')}:
        </StyledCandidateRecordInfoRowLabel>
        <StyledCandidateRecordInfoRowValue align={align}>
          <NotAssignComponent has={!!info.recruiter}>
            {info.recruiter && <UserBadgeComponent info={info.recruiter} />}
          </NotAssignComponent>
        </StyledCandidateRecordInfoRowValue>
      </StyledCandidateRecordInfoRow>
      <StyledCandidateRecordInfoRow>
        <StyledCandidateRecordInfoRowLabel>
          {t('Hr')}:
        </StyledCandidateRecordInfoRowLabel>
        <StyledCandidateRecordInfoRowValue align={align}>
          <NotAssignComponent has={!!info.hr}>
            {info.hr && <UserBadgeComponent info={info.hr} />}
          </NotAssignComponent>
        </StyledCandidateRecordInfoRowValue>
      </StyledCandidateRecordInfoRow>
      {info.techSpecialists && info.techSpecialists.length > 0 && (
        <StyledCandidateRecordInfoRow>
          <StyledCandidateRecordInfoRowLabel>
            {t('Tech specialist')}:
          </StyledCandidateRecordInfoRowLabel>
          <StyledCandidateRecordInfoRowValue align={align}>
            <StyledTechSpecialistsContainer>
              {info.techSpecialists.map(specialist => (
                <UserBadgeComponent key={specialist.id} info={specialist} />
              ))}
            </StyledTechSpecialistsContainer>
          </StyledCandidateRecordInfoRowValue>
        </StyledCandidateRecordInfoRow>
      )}

      {info.referral && (
        <StyledCandidateRecordInfoRow>
          <StyledCandidateRecordInfoRowLabel>
            {t('Referral')}:
          </StyledCandidateRecordInfoRowLabel>
          <StyledCandidateRecordInfoRowValue align={align}>
            <UserBadgeComponent info={info.referral} />
          </StyledCandidateRecordInfoRowValue>
        </StyledCandidateRecordInfoRow>
      )}
      {info.salary && !info.region?.name?.includes('Lambda') && (
        <StyledCandidateRecordInfoRow>
          <StyledCandidateRecordInfoRowLabel>
            {t('Salary')}
          </StyledCandidateRecordInfoRowLabel>
          <StyledCandidateRecordInfoRowValue
            align={align}
            onCopy={copySalary}
            onClick={() => setIsSalaryVisible(prev => !prev)}
          >
            <StyledSalaryRecordInfo>
              {isSalaryVisible && <EyeIcon color={theme.colors.LABEL} />}
              {!isSalaryVisible && (
                <EyeCrossedIcon color={theme.colors.LABEL} />
              )}
              {computedSalary}
            </StyledSalaryRecordInfo>
          </StyledCandidateRecordInfoRowValue>
        </StyledCandidateRecordInfoRow>
      )}
      {info.salaryBrutto && info.region?.name?.includes('Lambda') && (
        <StyledCandidateRecordInfoRow>
          <StyledCandidateRecordInfoRowLabel>
            {t('SalaryBrutto')}
          </StyledCandidateRecordInfoRowLabel>
          <StyledCandidateRecordInfoRowValue
            align={align}
            onCopy={copySalary}
            onClick={() => setIsSalaryVisible(prev => !prev)}
          >
            <StyledSalaryRecordInfo>
              {isSalaryVisible && <EyeIcon color={theme.colors.LABEL} />}
              {!isSalaryVisible && (
                <EyeCrossedIcon color={theme.colors.LABEL} />
              )}
              {computedSalaryBrutto}
            </StyledSalaryRecordInfo>
          </StyledCandidateRecordInfoRowValue>
        </StyledCandidateRecordInfoRow>
      )}
      {info.source && (
        <StyledCandidateRecordInfoRow>
          <StyledCandidateRecordInfoRowLabel>
            {t('Source')}:
          </StyledCandidateRecordInfoRowLabel>
          <StyledCandidateRecordInfoRowValue align={align}>
            {info.source.name}
          </StyledCandidateRecordInfoRowValue>
        </StyledCandidateRecordInfoRow>
      )}
      {info.region && (
        <StyledCandidateRecordInfoRow>
          <StyledCandidateRecordInfoRowLabel>
            {t('Region')}:
          </StyledCandidateRecordInfoRowLabel>
          <StyledCandidateRecordInfoRowValue align={align}>
            {info.region.name}
          </StyledCandidateRecordInfoRowValue>
        </StyledCandidateRecordInfoRow>
      )}
      {info.department && (
        <StyledCandidateRecordInfoRow>
          <StyledCandidateRecordInfoRowLabel>
            {t('Department')}:
          </StyledCandidateRecordInfoRowLabel>
          <StyledCandidateRecordInfoRowValue align={align}>
            {info.department.name}
          </StyledCandidateRecordInfoRowValue>
        </StyledCandidateRecordInfoRow>
      )}
      {info.comment && (
        <StyledCandidateRecordInfoRow>
          <StyledCandidateRecordInfoRowLabel>
            {t('Comment')}:
          </StyledCandidateRecordInfoRowLabel>
          <StyledCandidateRecordInfoRowComment>
            {info.comment}
          </StyledCandidateRecordInfoRowComment>
        </StyledCandidateRecordInfoRow>
      )}
      <GoogleCalendarInterviewBlockComponent record={info} />
      <StyledDivider />
      <TagBlock value={info.tags} onTagsChange={handleTagsChanged} />
    </StyledCandidateRecordInfo>
  );
};

export default CandidateRecordInfoBlockComponent;
